import { FC, memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { RouterState } from '@remix-run/router';

import { dialog, reactive, uuid } from './utils';
import { getSession, store, verify } from './state';
import router from './router';
import i18n from './i18n';
import { APIOptions, ConfirmDialog, Dialog, PrimeReactProvider, Toast } from './components/external';
import { ISession } from './models';

const tracker = ({ loaderData, matches }: RouterState) => {
  const { route: { id, handle }, params } = matches.findLast(({ route: { handle } }) => !handle?.exclude)!;
  const title = (typeof handle?.title === 'function' ? handle.title(loaderData[id], params) : handle?.title);

  const composite = ['Talented Wanted'];
  if (title) composite.unshift(title);

  document.title = composite.join(' | ');
};

if (router.state.initialized) tracker(router.state);
router.subscribe(tracker);

const session = reactive.detector({} as ISession);
session.subscribe(() => {
  router.revalidate();
});

store.dispatch(verify());
store.subscribe(() => {
  session.next(getSession(store.getState()) ?? {} as ISession);
});

export interface IApp {
  container?: HTMLElement;
}

export const App: FC<IApp> = memo(({ container }) => {
  const forceUpdate = useReducer(() => uuid.v1(), null!)[1];
  const register = useCallback((key: string, instance: any) => {
    if (key.startsWith('#')) Object.assign(window, { [key]: instance });
  }, []);

  useEffect(() => dialog.subscribe(forceUpdate), [forceUpdate]);

  const layout: Partial<APIOptions> = useMemo(() => ({
    appendTo: container,
    locale: 'en',
    ripple: true,
  }), [container]);

  return (
    <ReduxProvider store={store}>
      <I18nextProvider i18n={i18n}>
        <PrimeReactProvider value={layout}>
          <RouterProvider router={router} />
          <ConfirmDialog tagKey="default" />
          <Toast ref={(instance) => register('#toast', instance)} position="bottom-right" />
          { dialog.map((key, { header, template, footer, visible, closable }) => (<Dialog key={key} header={header} footer={footer} visible={visible} closable={closable} dismissableMask={closable} draggable={false} onHide={() => dialog.hide(key)}>{ template }</Dialog>)) }
        </PrimeReactProvider>
      </I18nextProvider>
    </ReduxProvider>
  );
});
